import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AlertCircle, Clock, CheckCircle, FileText, Plus, ArrowRight,
  TrendingUp, MapPin, MessageCircle, Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { generatePDF } from '../lib/pdf';
import { toast } from 'sonner';

const STATUS_COLORS = { levantada: '#3B82F6', en_proceso: '#F59E0B', turnada: '#8B5CF6', resuelta: '#10B981', cancelada: '#EF4444' };
const STATUS_LABELS = { levantada: 'Levantada', en_proceso: 'En Proceso', turnada: 'Turnada a Alcaldia', resuelta: 'Resuelta', cancelada: 'Cancelada' };

function StatCard({ stat }) {
  const Icon = stat.icon;
  return (
    <Card data-testid={`stat-card-${stat.key}`} className="bg-white border border-[#E4E4E7] rounded-md hover:-translate-y-1 hover:shadow-md transition-all duration-200">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${stat.color}10` }}>
            <Icon className="w-5 h-5" style={{ color: stat.color }} />
          </div>
          <TrendingUp className="w-4 h-4 text-[#52525B]" />
        </div>
        <p className="text-3xl font-black text-[#0A0A0A] tracking-tighter" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{stat.value}</p>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mt-1">{stat.label}</p>
      </CardContent>
    </Card>
  );
}

function RecentItem({ gestion }) {
  return (
    <Link to={`/gestion/${gestion.id}`} data-testid={`recent-item-${gestion.id}`}>
      <div className="flex items-center gap-3 p-3 rounded-md border border-[#E4E4E7] hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 cursor-pointer">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[gestion.status] }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A] truncate">{gestion.tipo}</p>
          <p className="text-xs text-[#52525B] flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {gestion.calle || 'Sin ubicacion'}, {gestion.colonia || ''}
          </p>
        </div>
        <Badge variant="outline" className="text-xs flex-shrink-0" style={{ borderColor: STATUS_COLORS[gestion.status], color: STATUS_COLORS[gestion.status] }}>
          {STATUS_LABELS[gestion.status]}
        </Badge>
      </div>
    </Link>
  );
}

export default function DashboardPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const handleDownloadPDF = useCallback(async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const subtitle = user?.distrito ? `Distrito ${user.distrito} · ${user.name}` : (user?.name || '');
      await generatePDF(reportRef.current, {
        title: 'Reporte Dashboard',
        subtitle,
        filename: `dashboard-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
      toast.success('PDF descargado');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo generar el PDF');
    } finally {
      setExporting(false);
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    api.get('/dashboard/stats')
      .then(res => { if (!cancelled) setStats(res.data); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  const statCards = useMemo(() => [
    { key: 'total', label: 'Total Gestiones', value: stats?.total || 0, icon: FileText, color: '#0052FF' },
    { key: 'levantadas', label: 'Levantadas', value: stats?.levantadas || 0, icon: AlertCircle, color: '#3B82F6' },
    { key: 'en_proceso', label: 'En Proceso', value: stats?.en_proceso || 0, icon: Clock, color: '#F59E0B' },
    { key: 'turnadas', label: 'Turnadas', value: stats?.turnadas || 0, icon: FileText, color: '#8B5CF6' },
    { key: 'resueltas', label: 'Resueltas', value: stats?.resueltas || 0, icon: CheckCircle, color: '#10B981' },
  ], [stats]);

  const pieData = useMemo(() => [
    { name: 'Levantadas', value: stats?.levantadas || 0, color: '#3B82F6' },
    { name: 'En Proceso', value: stats?.en_proceso || 0, color: '#F59E0B' },
    { name: 'Turnadas', value: stats?.turnadas || 0, color: '#8B5CF6' },
    { name: 'Resueltas', value: stats?.resueltas || 0, color: '#10B981' },
    { name: 'Canceladas', value: stats?.canceladas || 0, color: '#EF4444' },
  ].filter(d => d.value > 0), [stats]);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
      <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Dashboard</h1>
          <p className="text-base text-[#52525B] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Bienvenido, {user?.name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="download-pdf-btn"
            onClick={handleDownloadPDF}
            disabled={exporting}
            variant="outline"
            className="h-12 px-5 rounded-md font-bold border-[#E4E4E7] hover:bg-[#10B981] hover:text-white hover:border-[#10B981]"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Generando...' : 'Descargar PDF'}
          </Button>
          {(user?.role === 'brigadista' || user?.role === 'funcionario' || user?.role === 'admin') && (
            <Link to="/nueva-gestion">
              <Button data-testid="new-report-btn" className="bg-[#0052FF] hover:bg-[#0040CC] text-white h-12 px-6 rounded-md font-bold">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Reporte
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div ref={reportRef} className="space-y-8 bg-[#FAFAFA] p-2">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map(s => <StatCard key={s.key} stat={s} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-white border border-[#E4E4E7] rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Reportes por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.por_tipo?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.por_tipo}>
                  <XAxis dataKey="tipo" tick={{ fontSize: 11, fill: '#52525B' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#52525B' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '6px', border: '1px solid #E4E4E7', fontSize: '13px' }} />
                  <Bar dataKey="count" fill="#0052FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#52525B]"><p>Sin datos aun.</p></div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {pieData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-[#52525B]"><p>Sin datos</p></div>
            )}
            <div className="flex flex-wrap gap-3 mt-2">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                  <span className="text-xs text-[#52525B]">{d.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Actividad Reciente</CardTitle>
              <Link to="/mis-gestiones"><Button variant="ghost" size="sm" className="text-[#0052FF] text-xs">Ver todas <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.recientes?.length > 0
              ? stats.recientes.slice(0, 5).map(g => <RecentItem key={g.id} gestion={g} />)
              : <p className="text-sm text-[#52525B] text-center py-8">No hay actividad reciente</p>
            }
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#E4E4E7] rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Resumen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-md bg-[#FAFAFA] border border-[#E4E4E7]">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#0052FF]" />
                <div>
                  <p className="text-2xl font-black text-[#0A0A0A] tracking-tighter">{stats?.notificaciones_enviadas || 0}</p>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Notificaciones WhatsApp</p>
                </div>
              </div>
            </div>
            {stats?.por_seccion?.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3">Por Seccion Electoral</p>
                <div className="space-y-2">
                  {stats.por_seccion.slice(0, 5).map(s => (
                    <div key={s.seccion} className="flex items-center justify-between text-sm">
                      <span className="text-[#52525B]">{s.seccion}</span>
                      <span className="font-bold text-[#0A0A0A]">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {stats?.total > 0 && (
              <div className="p-4 rounded-md bg-[#10B981]/5 border border-[#10B981]/20">
                <p className="text-sm font-semibold text-[#10B981]">
                  Tasa de resolucion: {Math.round((stats?.resueltas / stats?.total) * 100)}%
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}
